import { useState } from 'react';
import { motion } from 'framer-motion';
import { Anchor, Users, UserCheck, Ship } from 'lucide-react';
import { 
  Header,
  StatsCard,
  SearchBox,
  VesselCategories,
  SearchResults,
  CrewDetailModal
} from '../components';
import { useSearch, useSummary } from '../hooks/useSearch';
import type { CrewMember } from '../types';

export default function SearchPage() {
  const {
    searchQuery,
    setSearchQuery,
    searchMode,
    setSearchMode,
    filters,
    setFilters,
    handleSearch,
    handleFilterSearch,
    results,
    isLoading,
    error,
  } = useSearch();

  const { data: summaryData } = useSummary();

  const [selectedCrew, setSelectedCrew] = useState<CrewMember | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleViewDetails = (crew: CrewMember) => {
    setSelectedCrew(crew);
    setIsModalOpen(true);
  };

  const handleCategoryClick = (category: string) => {
    setFilters({ ...filters, vesselCategory: category });
    setSearchMode('filters');
  };

  // Use summary data from API or fallback to defaults
  const stats = {
    activeMasters: summaryData?.by_rank?.MSTR || 0,
    chiefOfficers: summaryData?.by_rank?.COFF || 0,
    chiefEngineers: summaryData?.by_rank?.CENG || 0,
    totalVessels: summaryData?.total_vessels || 0,
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
            Intelligent Crew Management
          </h1>
          <p className="text-gray-500 text-lg max-w-2xl mx-auto">
            AI-powered vessel crew matching based on experience, qualifications, and availability
          </p>
        </motion.div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatsCard
            title="Active Masters"
            value={stats.activeMasters}
            icon={Anchor}
            color="blue"
            delay={0}
          />
          <StatsCard
            title="Chief Officers"
            value={stats.chiefOfficers}
            icon={Users}
            color="green"
            delay={0.05}
          />
          <StatsCard
            title="Chief Engineers"
            value={stats.chiefEngineers}
            icon={UserCheck}
            color="amber"
            delay={0.1}
          />
          <StatsCard
            title="Total Vessels"
            value={stats.totalVessels}
            icon={Ship}
            color="purple"
            delay={0.15}
          />
        </div>

        {/* Search Box */}
        <div className="mb-8">
          <SearchBox
            searchMode={searchMode}
            onSearchModeChange={setSearchMode}
            onSearch={handleSearch}
            onFilterSearch={handleFilterSearch}
            filters={filters}
            onFiltersChange={setFilters}
            isLoading={isLoading}
            initialQuery={searchQuery}
          />
        </div>

        {/* Vessel Categories */}
        {!results && !isLoading && (
          <div className="mb-8">
            <VesselCategories 
              onCategoryClick={handleCategoryClick}
              selectedCategory={filters.vesselCategory}
            />
          </div>
        )}

        {/* Search Results */}
        {(results || isLoading || error) && (
          <SearchResults
            results={results ?? null}
            isLoading={isLoading}
            error={error}
            onRetry={() => handleSearch(searchQuery)}
            onViewDetails={handleViewDetails}
          />
        )}
      </main>

      {/* Crew Detail Modal */}
      <CrewDetailModal
        crew={selectedCrew}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />

      {/* Footer */}
      <footer className="border-t border-gray-200 bg-white mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-primary-600 flex items-center justify-center">
                <Anchor className="w-4 h-4 text-white" />
              </div>
              <span className="text-sm text-gray-500">
                © 2025 CrewAI. Powered by LangGraph & OpenAI
              </span>
            </div>
            <div className="flex items-center gap-6 text-sm text-gray-500">
              <a href="#" className="hover:text-gray-900 transition-colors">Documentation</a>
              <a href="#" className="hover:text-gray-900 transition-colors">API</a>
              <a href="#" className="hover:text-gray-900 transition-colors">Support</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
